import { apiClient } from '@/api/api'
import type { AxiosResponse } from 'axios'
import type { SizesMetadata, UpdateSizePayload, UpdateSizeResponse, SurveyResults, SizeCategoryKey, CategorySizeData, BrandOption, SystemOption } from '@/types/sizes'
import { mapCategoryImage } from '@/utils/category-image-mapper'
import { primarySize } from '@/features/sizes/suggested-size'

export const getSizesMetadata = async (): Promise<AxiosResponse<SizesMetadata>> => {
  return apiClient.get<SizesMetadata>('/user/sizes/metadata')
}

/**
 * The backend serves brands as bare strings and size systems as {code, name};
 * the screens want {value, label}. Adapting here keeps the shape difference out
 * of the components. These paths are case-transformed, so `size_systems`
 * arrives as `sizeSystems`.
 */
export const getSupportedBrands = async (): Promise<BrandOption[]> => {
  const response = await apiClient.get<{ brands?: string[] }>('/size-recommendation/brands')
  return (response.data?.brands ?? []).map((brand) => ({ value: brand, label: brand }))
}

export const getSizeSystems = async (): Promise<SystemOption[]> => {
  const response = await apiClient.get<{ sizeSystems?: { code: string; name: string }[] }>(
    '/size-recommendation/size-systems'
  )
  return (response.data?.sizeSystems ?? []).map((system) => ({
    value: system.code,
    label: system.name
  }))
}

export const getUserSizes = async (
  surveyResponseId: number | string,
  brand: string = 'all',
  system: string = 'metric'
): Promise<AxiosResponse<SurveyResults>> => {
  const { fetchSurveyResults } = await import('@/features/survey/api/survey')

  const responseId = typeof surveyResponseId === 'string'
    ? Number.parseInt(surveyResponseId, 10)
    : surveyResponseId

  if (Number.isNaN(responseId) || responseId <= 0) {
    throw new Error('Invalid survey_response_id')
  }

  const results = await fetchSurveyResults({
    surveyResponseId: responseId,
    brand,
    system
  })

  // recommendedSize — the primary size from suggestedSize (sizing v2 shape
  // with a legacy fallback), else the first of the legacy array. The response
  // is camelised by the interceptor, so the array is `suggestedSizes`.
  const getRecommendedSize = (category: any) =>
    primarySize(category.suggestedSize) ?? category.suggestedSizes?.[0] ?? undefined

  const transformCategory = (category: any) => {
    const { recommendedSize: _, currentSize: __, ...rest } = category
    const userSelected = category.userSelectedSize
    return {
      ...rest,
      image: category.image ? mapCategoryImage(category.image) : category.image,
      currentSize: undefined,
      recommendedSize: getRecommendedSize(category),
      suggestedSize: category.suggestedSize ?? null,
      userSelectedSize:
        userSelected && (typeof userSelected === 'object' || typeof userSelected === 'string')
          ? userSelected
          : null
    }
  }

  // The survey response carries these only sometimes; fetch the catalogue
  // versions alongside so the dropdowns are populated either way.
  const [brands, systems] = await Promise.all([
    getSupportedBrands().catch(() => [] as BrandOption[]),
    getSizeSystems().catch(() => [] as SystemOption[])
  ])

  const transformedResults: SurveyResults = {
    brands: results.brands ?? brands,
    systems: results.systems ?? systems,
    tops: transformCategory(results.tops),
    bottoms: transformCategory(results.bottoms),
    dresses: results.dresses ? transformCategory(results.dresses) : null,
    outerwear: transformCategory(results.outerwear),
    lingerie: results.lingerie ? transformCategory(results.lingerie) : null,
    shoes: transformCategory(results.shoes)
  }

  return {
    data: transformedResults
  } as AxiosResponse<SurveyResults>
}

export const updateSize = async (
  category: SizeCategoryKey,
  payload: UpdateSizePayload,
  token: string
): Promise<AxiosResponse<UpdateSizeResponse>> => {
  const response = await apiClient.put<UpdateSizeResponse>(
    `/survey/sizes/${category}`,
    {
      size_international: payload.size_international,
      size_russian: payload.size_russian,
      size_european: payload.size_european
    },
    {
      headers: {
        Authorization: `Bearer ${token}`
      }
    }
  )
  
  return response
}

const SIZE_CATEGORIES: SizeCategoryKey[] = [
  'tops',
  'bottoms',
  'dresses',
  'outerwear',
  'lingerie',
  'shoes'
]

/**
 * There is no bulk reset endpoint, so this clears each category in turn.
 * A 404 means the category was never set — already reset, not a failure.
 * Rejects on any real failure so the UI cannot claim a reset that half-ran.
 */
export const resetSizes = async (): Promise<void> => {
  const results = await Promise.allSettled(
    SIZE_CATEGORIES.map((category) => apiClient.delete(`/survey/sizes/${category}`))
  )

  const failed = SIZE_CATEGORIES.filter((_, i) => {
    const result = results[i]
    if (result.status === 'fulfilled') return false
    return (result.reason as { response?: { status?: number } })?.response?.status !== 404
  })

  if (failed.length > 0) {
    throw new Error(`Failed to reset sizes for: ${failed.join(', ')}`)
  }
}